import { useCallback, useEffect, useRef } from 'react';

/*
 * Nitidez al ampliar: re-rasteriza la página visible desde el PDF cuando hay zoom.
 *
 * El visor rasteriza todas las páginas UNA vez a `renderScale` (2x) y las guarda como imagen;
 * el zoom es un `transform: scale()` sobre esa imagen, así que pasada cierta ampliación no hay
 * más detalle que mostrar y se ve borroso. Cuánto se nota depende de la pantalla: en un monitor
 * grande, a pantalla completa y con `devicePixelRatio` 2, una página puede ocupar ~4000 px
 * físicos al máximo zoom contra los ~1200 px que tiene la imagen base.
 *
 * Por eso la escala no es fija: se calcula desde el tamaño real en pantalla, el zoom máximo y
 * el `devicePixelRatio`, y sólo se rasteriza si mejora de verdad lo que ya hay.
 *
 * Cómo se aplica (importante): se cambia el `src` del `<img>` por DOM, sin tocar el estado
 * `pages`. Cambiar `pages` haría que `react-pageflip` reconstruya su colección de páginas
 * (`updateFromHtml`: destruye, recrea y recarga el render) en cada zoom. Como `pages` no
 * cambia, React nunca vuelve a escribir ese `src` y el cambio se sostiene.
 *
 * El tamaño mostrado del `<img>` lo fija el CSS (100%), así que la imagen de más resolución no
 * altera `clientWidth/clientHeight` y las cajas de enlaces del PDF siguen calzando.
 */

const LADO_MAXIMO = 4096;      // tope de canvas seguro en todos los navegadores (iOS es el estricto)
const MAXIMO_EN_CACHE = 4;     // páginas en alta que se conservan para volver a ampliar sin esperar
const ESPERA = 300;            // ms de calma antes de rasterizar, para no disparar por cada tic
const UMBRAL_ZOOM = 1.25;      // por debajo de esto la imagen base alcanza
const MEJORA_MINIMA = 1.15;    // si no supera la base por este factor, no vale la pena

const usePaginasNitidas = ({ pdfRef, pages, visibles, zoom, contenedorRef, anchoPagina, zoomMax }) => {
  const cacheRef = useRef(new Map());     // nº de página -> objectURL en alta (orden de inserción)
  const aplicadasRef = useRef(new Set()); // nº de páginas cuyo <img> está mostrando la versión en alta

  const aplicarSrc = useCallback((numero, url) => {
    const el = contenedorRef.current?.querySelector(`img[data-pagina="${numero}"]`);
    if (!el || el.getAttribute('src') === url) return;
    el.src = url;
  }, [contenedorRef]);

  const restaurarBase = useCallback(() => {
    aplicadasRef.current.forEach((numero) => {
      const original = pages[numero - 1]?.imageUrl;
      if (original) aplicarSrc(numero, original);
    });
    aplicadasRef.current.clear();
  }, [pages, aplicarSrc]);

  // Escala a la que hay que rasterizar para que al zoom máximo quede 1:1 con los píxeles físicos
  const escalaNecesaria = useCallback((datos) => {
    const base = datos.pdfWidth || datos.width;
    if (!base || !anchoPagina) return 0;
    const dpr = window.devicePixelRatio || 1;
    const lado = Math.max(datos.pdfWidth || 0, datos.pdfHeight || 0) || base;
    const escala = Math.min((anchoPagina * zoomMax * dpr) / base, LADO_MAXIMO / lado);
    return escala > (datos.renderScale || 1) * MEJORA_MINIMA ? escala : 0;
  }, [anchoPagina, zoomMax]);

  const clave = visibles.join('-');

  useEffect(() => {
    if (!visibles.length) return undefined;
    if (zoom < UMBRAL_ZOOM) { restaurarBase(); return undefined; }

    let cancelado = false;

    const temporizador = setTimeout(async () => {
      const pdf = pdfRef.current;
      if (!pdf) return;

      for (const numero of visibles) {
        if (cancelado) return;

        const enCache = cacheRef.current.get(numero);
        if (enCache) {
          aplicarSrc(numero, enCache);
          aplicadasRef.current.add(numero);
          continue;
        }

        const datos = pages[numero - 1];
        if (!datos) continue;
        const escala = escalaNecesaria(datos);
        if (!escala) continue; // la imagen base ya alcanza para esta pantalla

        let url = null;
        try {
          const page = await pdf.getPage(numero);
          const viewport = page.getViewport({ scale: escala });
          const canvas = document.createElement('canvas');
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
          url = await new Promise((resolver, rechazar) => {
            canvas.toBlob(
              (blob) => (blob ? resolver(URL.createObjectURL(blob)) : rechazar(new Error('sin blob'))),
              'image/jpeg',
              0.92,
            );
          });
          // soltar el búfer del canvas ya, sin esperar al recolector
          canvas.width = 0;
          canvas.height = 0;
        } catch (e) {
          console.warn('No se pudo rasterizar en alta la página', numero, e);
          continue; // se queda la imagen base
        }

        if (cancelado) { URL.revokeObjectURL(url); return; }

        cacheRef.current.set(numero, url);
        aplicarSrc(numero, url);
        aplicadasRef.current.add(numero);

        // Map conserva el orden de inserción: se sueltan las más viejas que no se estén viendo
        while (cacheRef.current.size > MAXIMO_EN_CACHE) {
          const [viejo, urlVieja] = cacheRef.current.entries().next().value;
          if (aplicadasRef.current.has(viejo)) break;
          cacheRef.current.delete(viejo);
          URL.revokeObjectURL(urlVieja);
        }
      }
    }, ESPERA);

    return () => {
      cancelado = true;
      clearTimeout(temporizador);
    };
    // `clave` resume qué páginas se están viendo; `visibles` cambia de identidad en cada render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, clave, pages, pdfRef, aplicarSrc, restaurarBase, escalaNecesaria]);

  // Al cargar otro PDF (o al desmontar) las imágenes en alta ya no sirven: hay que revocarlas
  useEffect(() => () => {
    cacheRef.current.forEach((url) => URL.revokeObjectURL(url));
    cacheRef.current.clear();
    aplicadasRef.current.clear();
  }, [pages]);
};

export default usePaginasNitidas;
